// src/Dashboard/pages/teacher/ListingAdherents/components/AdherentQuickDrawer.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBirthdayCake,
  FaCamera,
  FaCheck,
  FaCloudUploadAlt,
  FaEnvelope,
  FaIdCard,
  FaImage,
  FaLayerGroup,
  FaLink,
  FaMapMarkerAlt,
  FaPen,
  FaPhone,
  FaSave,
  FaSearch,
  FaSms,
  FaTimes,
  FaTrashAlt,
  FaUser,
  FaWeight,
} from "react-icons/fa";
import { MdMedicalServices } from "react-icons/md";
import { toast } from "react-toastify";

import CropPhotoModal, {
  toMasterUrl,
} from "../../../../../components/CropPhotoModal/CropPhotoModal";

import {
  uploadAdherentPhoto,
  toAbsolutePhotoUrl,
  cropAdherentPhoto,
} from "../../../../../services/adherentPhotoServices";

import {
  calculateAge,
  formatDateFR,
  getAdherentCategories,
  getAdherentPhoto,
  getStatusMeta,
} from "../utils/listingAdherentsUtils";

const STATUS_OPTIONS = [
  "",
  "Licencié",
  "Licencié Hors Club",
  "Inscrit",
  "Cours d'essai",
  "Licencié - Arrêt",
  "Non Licencié - Arrêt",
  "Ancien Licencié",
  "Ancien Licencié et Arrêt",
];

const inputClass =
  "w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100";

function getAdherentName(adherent) {
  const nom = String(adherent?.nom || "").trim().toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();
  return `${nom} ${prenom}`.trim() || "Adhérent";
}

function getGroupPillClasses(group) {
  const color = String(group?.color_groupe || "").trim();

  if (color.startsWith("bg-")) {
    return `${color} text-slate-900 ring-1 ring-black/5 dark:ring-white/10`;
  }

  return "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
}

function dateInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function cleanPhone(value) {
  return String(value || "").replace(/\s+/g, "").trim();
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}

function InfoTile({ icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white p-4 ring-1 ring-slate-200/70 dark:bg-slate-900 dark:ring-slate-700/70">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {icon}
        </span>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {label}
          </p>

          <p className="mt-1 break-words text-sm font-bold text-slate-900 dark:text-white">
            {value || "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

function ContactAction({ type, value, label, subtitle }) {
  if (!value) return null;

  const href =
    type === "mail"
      ? `mailto:${value}`
      : type === "sms"
      ? `sms:${cleanPhone(value)}`
      : `tel:${cleanPhone(value)}`;

  const Icon = type === "mail" ? FaEnvelope : type === "sms" ? FaSms : FaPhone;

  return (
    <a
      href={href}
      className="flex min-h-[72px] items-center gap-3 rounded-2xl bg-white px-4 py-3 text-left ring-1 ring-slate-200 transition hover:-translate-y-0.5 hover:bg-[#013E8E] hover:text-white hover:shadow-md dark:bg-slate-900 dark:ring-slate-700"
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
        <Icon />
      </span>

      <span className="min-w-0">
        <span className="block text-sm font-bold">{label}</span>
        <span className="block truncate text-xs opacity-70">{subtitle || value}</span>
      </span>
    </a>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
        {value}
      </p>
    </div>
  );
}

export default function AdherentQuickDrawer({
  isOpen,
  mode = "details",
  adherent,
  groups = [],
  seasons = [],
  saving,
  deletingId,
  isAdmin,
  isTeacher,

  onClose,
  onOpenDetails,
  onOpenEdit,
  onOpenGroups,
  onOpenPhoto,

  onSubmitAdherent,
  onSubmitPhoto,
  onSubmitGroups,
  onSubmitTeacherNotes,
  onSubmitBlessure,
  onDelete,
}) {
  const canManage = isAdmin || isTeacher;
  const fileInputRef = useRef(null);

  const [editForm, setEditForm] = useState({});
  const [photoForm, setPhotoForm] = useState({
    photo_url: "",
    autorisation_photo: true,
  });

  const [uploadMode, setUploadMode] = useState("upload");
  const [outputFormat, setOutputFormat] = useState("webp");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imgVersion, setImgVersion] = useState(0);
  const [imgOk, setImgOk] = useState(true);
  const [isCropOpen, setIsCropOpen] = useState(false);

  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
  const [groupSearch, setGroupSearch] = useState("");

  const [teacherNotes, setTeacherNotes] = useState("");
  const [isBlessed, setIsBlessed] = useState(false);
  const [dateReprise, setDateReprise] = useState("");
  const [notesAdherent, setNotesAdherent] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!adherent) return;

    setEditForm({
      nom: adherent.nom || "",
      prenom: adherent.prenom || "",
      email: adherent.email || "",
      email_secondaire: adherent.email_secondaire || "",
      tel: adherent.tel || "",
      tel_secondaire: adherent.tel_secondaire || "",
      adresse: adherent.adresse || "",
      cp: adherent.cp || "",
      commune: adherent.commune || "",
      representant_legal: adherent.representant_legal || "",
      genre: adherent.genre || "",
      statut_inscription: adherent.statut_inscription || "",
      date_naissance: dateInputValue(adherent.date_naissance),
      lieu_naissance: adherent.lieu_naissance || "",
      poids: adherent.poids || "",
      licence_number: adherent.licence_number || "",
      saison_active: adherent.saison_active || "",
      autorisation_photo: adherent.autorisation_photo ?? true,
      notes_adherent: adherent.notes_adherent || "",
      passsport_number: adherent.passsport_number || "",
    });

    setPhotoForm({
      photo_url: adherent.photo_url || "",
      autorisation_photo: adherent.autorisation_photo ?? true,
    });

    setTeacherNotes(adherent.notes_enseignant || "");
    setIsBlessed(!!adherent.is_blessed);
    setDateReprise(dateInputValue(adherent.date_reprise));
    setNotesAdherent(adherent.notes_adherent || "");

    setSelectedGroupIds(
      new Set(
        (adherent.Groups || [])
          .map((group) => group?.id)
          .filter(Boolean)
          .map(String)
      )
    );

    setGroupSearch("");
    setUploadMode("upload");
    setOutputFormat("webp");
    setUploadProgress(0);
    setIsUploading(false);
    setImgOk(true);
    setImgVersion((v) => v + 1);
  }, [adherent, isOpen, mode]);

  const filteredGroups = useMemo(() => {
    const query = groupSearch.trim().toLowerCase();

    return groups
      .slice()
      .sort((a, b) =>
        String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr")
      )
      .filter((group) => {
        if (!query) return true;

        return [group?.nom, group?.dojo_nom, group?.Dojo?.dojo_nom]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);
      });
  }, [groups, groupSearch]);

  const selectedGroups = useMemo(() => {
    const map = new Map(groups.map((group) => [String(group.id), group]));

    return Array.from(selectedGroupIds)
      .map((id) => map.get(String(id)))
      .filter(Boolean);
  }, [groups, selectedGroupIds]);

  if (!isOpen || !adherent) return null;

  const photo = getAdherentPhoto(adherent);
  const status = getStatusMeta(adherent?.statut_inscription);
  const age = calculateAge(adherent?.date_naissance);
  const { ageCategory, weightCategory } = getAdherentCategories(adherent);
  const name = getAdherentName(adherent);

  const tel = adherent?.tel || "";
  const telSecondaire = adherent?.tel_secondaire || "";
  const email = adherent?.email || "";
  const emailSecondaire = adherent?.email_secondaire || "";

  const title =
    mode === "edit"
      ? "Fiche / profil adhérent"
      : mode === "groups"
      ? "Gérer les groupes"
      : mode === "photo"
      ? "Modifier la photo"
      : "Fiche adhérent";

  const absolutePhoto = toAbsolutePhotoUrl(photoForm.photo_url);
  const previewPhoto = photoForm.autorisation_photo
    ? absolutePhoto || photo
    : "/img/interdiction.jpg";

  const toggleGroup = (groupId) => {
    const key = String(groupId);

    setSelectedGroupIds((prev) => {
      const next = new Set(prev);

      if (next.has(key)) next.delete(key);
      else next.add(key);

      return next;
    });
  };

  const openFilePicker = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    event.stopPropagation();

    const file = event.target.files?.[0];
    if (!file) return;

    if (!adherent?.id) {
      toast.error("ID adhérent manquant.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const res = await uploadAdherentPhoto(adherent.id, file, {
        format: outputFormat,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;

          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          setUploadProgress(percent);
        },
      });

      const newPhotoUrl = res?.photo_url || "";

      setPhotoForm((prev) => ({
        ...prev,
        photo_url: newPhotoUrl,
      }));

      setImgOk(true);
      setImgVersion((v) => v + 1);

      if (newPhotoUrl) {
        await onSubmitPhoto?.({
          photo_url: newPhotoUrl,
          autorisation_photo: photoForm.autorisation_photo,
          _stayInMode: true,
        });
      }

      toast.success("Photo uploadée et affectée à l’adhérent.");
    } catch (error) {
      console.error("Erreur upload photo :", error);
      toast.error(`Erreur upload : ${error?.message || "inconnue"}`);
    } finally {
      setIsUploading(false);

      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const submitEdit = (event) => {
    event?.preventDefault?.();
    onSubmitAdherent?.(editForm);
  };

  const submitPhoto = (event) => {
    event?.preventDefault?.();

    if (isUploading) {
      toast.info("Patiente, l’upload est en cours…");
      return;
    }

    onSubmitPhoto?.(photoForm);
  };

  const submitGroups = (event) => {
    event?.preventDefault?.();
    onSubmitGroups?.(Array.from(selectedGroupIds).map(Number));
  };

  return (
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {title}
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white">
              {name}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {mode === "details" && (
            <div>
              <div className="relative">
                <div className="aspect-[4/3] max-h-[460px] overflow-hidden bg-slate-100 dark:bg-slate-800">
                  <img
                    src={photo}
                    alt={name}
                    className="h-full w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.src = "/img/user_avatar.jpg";
                    }}
                  />
                </div>

                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/95 via-slate-950/45 to-transparent p-4 pt-20">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-bold ${status.className}`}
                    >
                      {adherent?.statut_inscription || "Sans statut"}
                    </span>

                    {Number.isFinite(age) && (
                      <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-slate-900">
                        {age} ans
                      </span>
                    )}

                    <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700 ring-1 ring-blue-200">
                      {ageCategory}
                    </span>

                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-200">
                      {weightCategory}
                    </span>

                    {adherent?.is_blessed && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white">
                        <MdMedicalServices />
                        Blessure / absence
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-5 p-4">
                {(tel || telSecondaire || email || emailSecondaire) && (
                  <section className="rounded-[24px] bg-red-50 p-4 ring-1 ring-red-200/70 dark:bg-red-900/10 dark:ring-red-800/50">
                    <div className="mb-4 flex items-center gap-3">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-red-600 text-white">
                        <FaPhone />
                      </span>

                      <div>
                        <h3 className="text-base font-bold text-red-950 dark:text-red-100">
                          Contacts rapides / urgence
                        </h3>

                        <p className="text-sm text-red-800/80 dark:text-red-100/70">
                          Appeler, envoyer un SMS ou envoyer un email depuis la fiche.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <ContactAction type="tel" value={tel} label="Appeler principal" subtitle={tel} />
                      <ContactAction type="sms" value={tel} label="SMS principal" subtitle={tel} />
                      <ContactAction type="tel" value={telSecondaire} label="Appeler secondaire" subtitle={telSecondaire} />
                      <ContactAction type="sms" value={telSecondaire} label="SMS secondaire" subtitle={telSecondaire} />
                      <ContactAction type="mail" value={email} label="Email principal" subtitle={email} />
                      <ContactAction type="mail" value={emailSecondaire} label="Email secondaire" subtitle={emailSecondaire} />
                    </div>
                  </section>
                )}

                <section className="rounded-[24px] bg-slate-50 p-4 ring-1 ring-slate-200/70 dark:bg-slate-800/40 dark:ring-slate-700/70">
                  <h3 className="mb-4 text-base font-bold text-slate-900 dark:text-white">
                    Informations adhérent
                  </h3>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <InfoTile icon={<FaUser />} label="Nom" value={adherent?.nom} />
                    <InfoTile icon={<FaUser />} label="Prénom" value={adherent?.prenom} />
                    <InfoTile icon={<FaBirthdayCake />} label="Date de naissance" value={formatDateFR(adherent?.date_naissance)} />
                    <InfoTile icon={<FaMapMarkerAlt />} label="Lieu de naissance" value={adherent?.lieu_naissance} />
                    <InfoTile icon={<FaUser />} label="Genre" value={adherent?.genre} />
                    <InfoTile icon={<FaWeight />} label="Poids" value={adherent?.poids ? `${Number(adherent.poids)} kg` : "—"} />
                    <InfoTile icon={<FaIdCard />} label="Licence" value={adherent?.licence_number} />
                    <InfoTile icon={<FaIdCard />} label="Pass’Sport" value={adherent?.passsport_number} />
                    <InfoTile icon={<FaUser />} label="Catégorie âge" value={ageCategory} />
                    <InfoTile icon={<FaWeight />} label="Catégorie poids" value={weightCategory} />
                    <InfoTile icon={<FaUser />} label="Saison" value={adherent?.saison_active} />
                    <InfoTile icon={<FaUser />} label="Statut" value={adherent?.statut_inscription} />
                    <InfoTile icon={<FaUser />} label="Représentant légal" value={adherent?.representant_legal} />
                    <InfoTile icon={<FaMapMarkerAlt />} label="Adresse" value={[adherent?.adresse, adherent?.cp, adherent?.commune].filter(Boolean).join(" ")} />
                  </div>
                </section>

                <section className="rounded-[24px] bg-slate-50 p-4 ring-1 ring-slate-200/70 dark:bg-slate-800/40 dark:ring-slate-700/70">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-base font-bold text-slate-900 dark:text-white">
                        Groupes
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Cliquez sur “Gérer les groupes” pour modifier les groupes.
                      </p>
                    </div>

                    {canManage && (
                      <button
                        type="button"
                        onClick={onOpenGroups}
                        className="inline-flex items-center gap-2 rounded-2xl bg-violet-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-violet-700"
                      >
                        <FaLayerGroup />
                        Gérer
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {(adherent?.Groups || []).length === 0 ? (
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
                        Aucun groupe
                      </span>
                    ) : (
                      adherent.Groups.map((group) => (
                        <button
                          key={group.id || group.nom}
                          type="button"
                          onClick={canManage ? onOpenGroups : undefined}
                          className={`rounded-full px-3 py-1 text-xs font-bold ${getGroupPillClasses(group)}`}
                        >
                          {group.nom}
                        </button>
                      ))
                    )}
                  </div>
                </section>

                <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div className="rounded-[24px] bg-white p-4 ring-1 ring-slate-200/70 dark:bg-slate-900 dark:ring-slate-700/70">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <h3 className="font-bold text-slate-900 dark:text-white">
                          Notes adhérent
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Informations générales.
                        </p>
                      </div>
                    </div>

                    <p className="min-h-24 whitespace-pre-wrap rounded-2xl bg-slate-50 p-3 text-sm leading-6 text-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
                      {adherent?.notes_adherent || "Aucune note."}
                    </p>
                  </div>

                  <div className="rounded-[24px] bg-white p-4 ring-1 ring-slate-200/70 dark:bg-slate-900 dark:ring-slate-700/70">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <h3 className="font-bold text-slate-900 dark:text-white">
                          Notes enseignant
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Notes internes de suivi.
                        </p>
                      </div>

                      {canManage && (
                        <button
                          type="button"
                          onClick={() => onSubmitTeacherNotes?.({ notes_enseignant: teacherNotes })}
                          disabled={saving}
                          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-blue-700 disabled:opacity-60"
                        >
                          <FaSave />
                          Sauver
                        </button>
                      )}
                    </div>

                    <textarea
                      value={teacherNotes}
                      onChange={(event) => setTeacherNotes(event.target.value)}
                      disabled={!canManage}
                      placeholder="Notes pédagogiques, comportement, point d’attention..."
                      className="min-h-24 w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-700 outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-70 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200"
                    />
                  </div>
                </section>

                <section className="rounded-[24px] bg-red-50 p-4 ring-1 ring-red-200/70 dark:bg-red-900/10 dark:ring-red-800/50">
                  <div className="flex items-start gap-3">
                    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-600 text-white">
                      <MdMedicalServices />
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h3 className="font-bold text-red-950 dark:text-red-100">
                            Blessure / absence
                          </h3>
                          <p className="text-sm text-red-800/80 dark:text-red-100/70">
                            Suivi rapide pour les enseignants.
                          </p>
                        </div>

                        {canManage && (
                          <button
                            type="button"
                            onClick={() =>
                              onSubmitBlessure?.({
                                is_blessed: isBlessed,
                                date_reprise: dateReprise || null,
                                notes_adherent: notesAdherent || null,
                              })
                            }
                            disabled={saving}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-red-700 disabled:opacity-60"
                          >
                            <FaSave />
                            Sauver
                          </button>
                        )}
                      </div>

                      <label className="mt-4 flex items-center gap-3 text-sm font-bold text-red-950 dark:text-red-50">
                        <input
                          type="checkbox"
                          checked={isBlessed}
                          disabled={!canManage}
                          onChange={(event) => setIsBlessed(event.target.checked)}
                          className="h-5 w-5 rounded border-red-300 accent-red-600"
                        />
                        Judoka malade, blessé ou absent
                      </label>

                      {isBlessed && (
                        <div className="mt-3 grid grid-cols-1 gap-3">
                          <Field label="Date de reprise prévue">
                            <input
                              type="date"
                              value={dateReprise || ""}
                              disabled={!canManage}
                              onChange={(event) => setDateReprise(event.target.value)}
                              className={inputClass}
                            />
                          </Field>

                          <Field label="Note absence / blessure">
                            <textarea
                              value={notesAdherent}
                              disabled={!canManage}
                              onChange={(event) => setNotesAdherent(event.target.value)}
                              placeholder="Ex : entorse cheville droite, repos 10 jours..."
                              className={`${inputClass} min-h-24`}
                            />
                          </Field>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              </div>
            </div>
          )}

          {mode === "edit" && (
            <form onSubmit={submitEdit} className="space-y-5 p-4">
              <section className="rounded-[24px] bg-slate-50 p-4 ring-1 ring-slate-200/70 dark:bg-slate-800/40 dark:ring-slate-700/70">
                <h3 className="mb-4 text-base font-bold text-slate-900 dark:text-white">
                  Fiche / profil adhérent
                </h3>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Nom">
                    <input value={editForm.nom || ""} onChange={(e) => setEditForm((p) => ({ ...p, nom: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Prénom">
                    <input value={editForm.prenom || ""} onChange={(e) => setEditForm((p) => ({ ...p, prenom: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Genre">
                    <select value={editForm.genre || ""} onChange={(e) => setEditForm((p) => ({ ...p, genre: e.target.value }))} className={inputClass}>
                      <option value="">—</option>
                      <option value="Masculin">Masculin</option>
                      <option value="Féminin">Féminin</option>
                    </select>
                  </Field>

                  <Field label="Date de naissance">
                    <input type="date" value={editForm.date_naissance || ""} onChange={(e) => setEditForm((p) => ({ ...p, date_naissance: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Lieu de naissance">
                    <input value={editForm.lieu_naissance || ""} onChange={(e) => setEditForm((p) => ({ ...p, lieu_naissance: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Licence">
                    <input value={editForm.licence_number || ""} onChange={(e) => setEditForm((p) => ({ ...p, licence_number: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Poids">
                    <input type="number" step="0.1" value={editForm.poids || ""} onChange={(e) => setEditForm((p) => ({ ...p, poids: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Pass’Sport">
                    <input value={editForm.passsport_number || ""} onChange={(e) => setEditForm((p) => ({ ...p, passsport_number: e.target.value }))} className={inputClass} />
                  </Field>
                </div>
              </section>

              <section className="rounded-[24px] bg-slate-50 p-4 ring-1 ring-slate-200/70 dark:bg-slate-800/40 dark:ring-slate-700/70">
                <h3 className="mb-4 text-base font-bold text-slate-900 dark:text-white">
                  Coordonnées
                </h3>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Email">
                    <input value={editForm.email || ""} onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Email secondaire">
                    <input value={editForm.email_secondaire || ""} onChange={(e) => setEditForm((p) => ({ ...p, email_secondaire: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Téléphone">
                    <input value={editForm.tel || ""} onChange={(e) => setEditForm((p) => ({ ...p, tel: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Téléphone secondaire">
                    <input value={editForm.tel_secondaire || ""} onChange={(e) => setEditForm((p) => ({ ...p, tel_secondaire: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Représentant légal">
                    <input value={editForm.representant_legal || ""} onChange={(e) => setEditForm((p) => ({ ...p, representant_legal: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Adresse">
                    <input value={editForm.adresse || ""} onChange={(e) => setEditForm((p) => ({ ...p, adresse: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Code postal">
                    <input value={editForm.cp || ""} onChange={(e) => setEditForm((p) => ({ ...p, cp: e.target.value }))} className={inputClass} />
                  </Field>

                  <Field label="Commune">
                    <input value={editForm.commune || ""} onChange={(e) => setEditForm((p) => ({ ...p, commune: e.target.value }))} className={inputClass} />
                  </Field>
                </div>
              </section>

              <section className="rounded-[24px] bg-slate-50 p-4 ring-1 ring-slate-200/70 dark:bg-slate-800/40 dark:ring-slate-700/70">
                <h3 className="mb-4 text-base font-bold text-slate-900 dark:text-white">
                  Infos club
                </h3>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Statut inscription">
                    <select value={editForm.statut_inscription || ""} onChange={(e) => setEditForm((p) => ({ ...p, statut_inscription: e.target.value }))} className={inputClass}>
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status || "empty"} value={status}>
                          {status || "—"}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Saison active">
                    <select value={editForm.saison_active || ""} onChange={(e) => setEditForm((p) => ({ ...p, saison_active: e.target.value }))} className={inputClass}>
                      <option value="">—</option>
                      {(seasons || []).map((season) => (
                        <option key={season} value={season}>
                          {season}
                        </option>
                      ))}
                      {editForm.saison_active && !seasons.includes(editForm.saison_active) && (
                        <option value={editForm.saison_active}>
                          {editForm.saison_active}
                        </option>
                      )}
                    </select>
                  </Field>

                  <div className="sm:col-span-2">
                    <Field label="Notes adhérent">
                      <textarea value={editForm.notes_adherent || ""} onChange={(e) => setEditForm((p) => ({ ...p, notes_adherent: e.target.value }))} className={`${inputClass} min-h-28`} />
                    </Field>
                  </div>
                </div>
              </section>
            </form>
          )}

          {mode === "photo" && (
            <form onSubmit={submitPhoto} className="space-y-5 p-4">
              <section className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                    <FaCamera />
                  </span>

                  <div>
                    <h3 className="font-bold text-slate-900 dark:text-white">
                      Photo adhérent
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      Sur smartphone, le bouton permet de prendre une photo directement.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" onClick={() => setUploadMode("upload")} className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold ring-1 transition ${uploadMode === "upload" ? "bg-indigo-600 text-white ring-indigo-600" : "bg-white text-slate-700 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"}`}>
                    <FaCloudUploadAlt />
                    Uploader
                  </button>

                  <button type="button" onClick={() => setUploadMode("url")} className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold ring-1 transition ${uploadMode === "url" ? "bg-indigo-600 text-white ring-indigo-600" : "bg-white text-slate-700 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"}`}>
                    <FaLink />
                    Lien URL
                  </button>
                </div>
              </section>

              <div className="overflow-hidden rounded-3xl bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
                <img src={`${previewPhoto || "/img/user_avatar.jpg"}?v=${imgVersion}`} alt={name} className="mx-auto max-h-[420px] w-full object-contain" onError={(e) => { setImgOk(false); e.currentTarget.src = "/img/user_avatar.jpg"; }} />
              </div>

              {uploadMode === "upload" ? (
                <section className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
                  <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button type="button" onClick={openFilePicker} disabled={isUploading} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:opacity-60">
                      <FaCloudUploadAlt />
                      Prendre / choisir une photo
                    </button>

                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                        Format
                      </span>

                      <select className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white" value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)} disabled={isUploading}>
                        <option value="webp">WEBP</option>
                        <option value="jpg">JPG</option>
                        <option value="png">PNG</option>
                      </select>
                    </div>
                  </div>

                  {isUploading && (
                    <div className="mt-4">
                      <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                        <div className="h-full rounded-full bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" style={{ width: `${uploadProgress}%` }} />
                      </div>

                      <p className="mt-1 text-xs font-bold text-slate-500 dark:text-slate-400">
                        Upload en cours : {uploadProgress}%
                      </p>
                    </div>
                  )}
                </section>
              ) : (
                <Field label="URL de la photo">
                  <input value={photoForm.photo_url || ""} onChange={(e) => { setPhotoForm((p) => ({ ...p, photo_url: e.target.value })); setImgOk(true); setImgVersion((v) => v + 1); }} placeholder="/uploads/... ou https://..." className={inputClass} />
                </Field>
              )}

              <label className="flex items-center gap-3 rounded-2xl bg-slate-50 p-4 text-sm font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800/40 dark:text-slate-200 dark:ring-slate-700">
                <input type="checkbox" checked={!!photoForm.autorisation_photo} onChange={(e) => setPhotoForm((p) => ({ ...p, autorisation_photo: e.target.checked }))} className="h-5 w-5 rounded accent-violet-600" />
                Autorisation photo
              </label>

              {photoForm.photo_url && (
                <button type="button" onClick={() => setIsCropOpen(true)} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">
                  <FaImage />
                  Recadrer / zoomer
                </button>
              )}

              {!imgOk && (
                <p className="text-sm font-bold text-red-600">
                  Impossible de charger l’image.
                </p>
              )}
            </form>
          )}

          {mode === "groups" && (
            <form onSubmit={submitGroups} className="space-y-5 p-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <Kpi label="Sélectionnés" value={selectedGroupIds.size} />
                <Kpi label="Disponibles" value={groups.length} />
                <Kpi label="Affichés" value={filteredGroups.length} />
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                  <input type="text" value={groupSearch} onChange={(event) => setGroupSearch(event.target.value)} placeholder="Rechercher un groupe ou un dojo..." className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white" />
                </div>
              </div>

              <div className="grid max-h-[420px] gap-2 overflow-auto pr-1 sm:grid-cols-2">
                {filteredGroups.map((group) => {
                  const checked = selectedGroupIds.has(String(group.id));
                  const dojoName = group?.dojo_nom || group?.Dojo?.dojo_nom || "";

                  return (
                    <label key={group.id} className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-3 py-3 transition ${checked ? "border-indigo-200 bg-indigo-50 ring-1 ring-indigo-200 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:ring-indigo-500/20" : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:hover:bg-slate-900"}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleGroup(group.id)} className="h-5 w-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400" />

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-bold ${getGroupPillClasses(group)}`}>
                            {group.nom}
                          </span>

                          {checked && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                              <FaCheck />
                              Sélectionné
                            </span>
                          )}
                        </div>

                        {dojoName && (
                          <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                            Dojo : {dojoName}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </form>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white/95 p-3 dark:border-slate-800 dark:bg-slate-900/95">
          {mode === "details" ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {canManage && (
                <button type="button" onClick={onOpenEdit} className="inline-flex min-h-[58px] items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-3 text-sm font-bold text-white transition hover:bg-indigo-800">
                  <FaPen />
                  Profil
                </button>
              )}

              {canManage && (
                <button type="button" onClick={onOpenPhoto} className="inline-flex min-h-[58px] items-center justify-center gap-2 rounded-2xl bg-indigo-50 px-3 py-3 text-sm font-bold text-indigo-700 transition hover:bg-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-200">
                  <FaCamera />
                  Photo
                </button>
              )}

              {canManage && (
                <button type="button" onClick={onOpenGroups} className="inline-flex min-h-[58px] items-center justify-center gap-2 rounded-2xl bg-violet-50 px-3 py-3 text-sm font-bold text-violet-700 transition hover:bg-violet-100 dark:bg-violet-900/20 dark:text-violet-200">
                  <FaLayerGroup />
                  Groupes
                </button>
              )}

              {isAdmin && (
                <button type="button" disabled={deletingId === adherent.id} onClick={() => onDelete?.(adherent)} className="inline-flex min-h-[58px] items-center justify-center gap-2 rounded-2xl bg-red-600 px-3 py-3 text-sm font-bold text-white transition hover:bg-red-700 disabled:opacity-60">
                  <FaTrashAlt />
                  Supprimer
                </button>
              )}
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onOpenDetails} className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">
                <FaTimes />
                Annuler
              </button>

              <button type="button" disabled={saving || isUploading} onClick={mode === "edit" ? submitEdit : mode === "photo" ? submitPhoto : submitGroups} className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60">
                <FaSave />
                {saving || isUploading ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          )}
        </div>
      </aside>

      <CropPhotoModal
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        entityId={adherent?.id}
        imageUrl={`${toAbsolutePhotoUrl(toMasterUrl(photoForm.photo_url))}?v=${imgVersion}`}
        onCropped={async (newUrl) => {
          setPhotoForm((prev) => ({
            ...prev,
            photo_url: newUrl,
          }));

          setImgVersion((v) => v + 1);

          if (newUrl) {
            await onSubmitPhoto?.({
              photo_url: newUrl,
              autorisation_photo: photoForm.autorisation_photo,
              _stayInMode: true,
            });
          }
        }}
        format="webp"
        cropAction={cropAdherentPhoto}
      />
    </div>
  );
}